import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Group,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconBrandWhatsapp, IconMail } from "@tabler/icons-react";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { DEFAULT_PAGINATION } from "@constants/index";

import { useGetEnrollments } from "@hooks/queries/masterclass/useGetEnrollments";


interface EnrollmentTableProps {
  masterclassId: string | null;
}

export const EnrollmentTable = ({ masterclassId }: EnrollmentTableProps) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGINATION.pageSize;

  const { data, isLoading, isError, isSuccess, isRefetching } = useGetEnrollments({
    masterclassId: masterclassId || undefined,
    isEnabled: !!masterclassId
  });

  const total = data?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginatedEnrollments = useMemo(
    () => data?.slice((currentPage - 1) * pageSize, currentPage * pageSize) || [],
    [data, currentPage, pageSize]
  );

  useEffect(() => { setPage(1); }, [masterclassId]);
  useEffect(() => { setPage(previous => Math.min(previous, pageCount)); }, [pageCount]);

  const rows = useMemo(() => {
    return paginatedEnrollments.map(item => (
      <Table.Tr key={item._id}>
        <Table.Td>
          <Text fz={14} fw={500}>
            {item.first_name} {item.surname}
          </Text>
        </Table.Td>

        <Table.Td>
          <Stack gap={0}>
            <Group gap={5}>
              <IconMail size={14} color="var(--clr-gray-400)" />
              <Text fz={13}>{item.email || "N/A"}</Text>
            </Group>
            <Group gap={5}>
              <IconBrandWhatsapp size={14} color="var(--clr-green-500)" />
              <Text fz={13}>{item.whatsapp_phone_number || item.phone_number}</Text>
            </Group>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Badge color={item.gender === 'male' ? 'blue' : item.gender === 'female' ? 'pink' : 'gray'}>
            {t(item.gender)}
          </Badge>
        </Table.Td>

        <Table.Td>
          <Text fz={14}>
            {item.created_at ? formatDateToString(new Date(item.created_at)) : "-"}
          </Text>
        </Table.Td>

        <Table.Td>
          <Text fz={13}>{item.residence_location || "-"}</Text>
        </Table.Td>

        <Table.Td>
          <Text fz={13}>{item.industry || "-"}</Text>
        </Table.Td>

        <Table.Td>
          <Text fz={13} lineClamp={2} title={item.participation_reason}>
            {item.participation_reason || "-"}
          </Text>
        </Table.Td>

        <Table.Td>
          <Badge color={item.payment_transaction_details ? "green" : "orange"} variant="light">
            {item.payment_transaction_details ? t("paid") : t("pending")}
          </Badge>
        </Table.Td>
      </Table.Tr>
    ));
  }, [paginatedEnrollments, i18n.language, t]);

  if (!masterclassId) {
    return (
      <Table withRowBorders mt="md" withTableBorder>
        <Table.Tbody>
          <TableEmptyRow 
            colSpan={8} 
            message={t("please-select-masterclass-to-view-enrollments", "Please select a masterclass to view enrollments")} 
          />
        </Table.Tbody>
      </Table>
    );
  }

  return (
    <Stack gap="md" mb="8rem">
    <Table.ScrollContainer minWidth={1200}>
    <Table
      withRowBorders
      mt="md"
      mb="xl"
      miw={1200}
      withTableBorder
      highlightOnHover
      highlightOnHoverColor="var(--clr-gray-100)">      <Table.Thead>
        <Table.Tr bg="var(--clr-gray-100)">
          <Table.Th>{t("participant")}</Table.Th>
          <Table.Th>{t("contact")}</Table.Th>
          <Table.Th>{t("gender")}</Table.Th>
          <Table.Th>{t("enrollment-date")}</Table.Th>
          <Table.Th>{t("residence-location")}</Table.Th>
          <Table.Th>{t("industry")}</Table.Th>
          <Table.Th>{t("participation-reason")}</Table.Th>
          <Table.Th>{t("payment-status")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {renderLoadingErrorData({
          isLoading: isLoading || isRefetching,
          isError,
          isSuccess,
          dataLength: data?.length || 0,
          emptyDataComponent: (
            <TableEmptyRow colSpan={8} message={t("no-enrollments")} />
          ),
          errorComponent: (
            <TableErrorRow colSpan={8} message={t("encountered-error")} />
          ),
          loadingComponent: (
            <TableSkeletonRows
              columns={8}
              skeletonProps={{
                widths: ["lg", "lg", "sm", "md", "md", "md", "lg", "md"],
              }}
            />
          ),
          dataComponent: rows,
        })}
      </Table.Tbody>
    </Table>
    </Table.ScrollContainer>
    {isSuccess && !isError && (
      <TablePagination
        paginationData={{ page: currentPage, pageSize, pageCount, total }}
        paginatedRows={paginatedEnrollments.length}
        onPageChange={setPage}
        isLoading={isLoading || isRefetching}
      />
    )}
    </Stack>
  );
};
